import { Input } from '@/components/ui/input';

/**
 * A number of minutes entered as a value plus a unit, because 1440 is not a
 * thought anyone has — "1 day" is.
 *
 * The unit is inferred from the stored value rather than held in state, so
 * reopening a step shows the same units it was entered with instead of
 * normalising every wait to minutes.
 */
export function DurationInput({
  id,
  label,
  minutes,
  onChange,
  disabled,
}: {
  id: string;
  /**
   * What this duration is. Required because the control sits inside a sentence
   * with no visible `<label>` — without it a screen reader reads two bare
   * inputs.
   */
  label: string;
  minutes: number;
  onChange: (minutes: number) => void;
  disabled?: boolean;
}): React.ReactElement {
  const unit = minutes > 0 && minutes % 1440 === 0 ? 1440 : minutes % 60 === 0 ? 60 : 1;
  const value = unit === 1 ? minutes : minutes / unit;

  return (
    <div className="flex items-center gap-1.5">
      <Input
        id={id}
        type="number"
        min={0}
        aria-label={label}
        className="h-9 w-16"
        disabled={disabled}
        value={value}
        onChange={(e) => {
          const next = Number(e.target.value);
          if (Number.isFinite(next) && next >= 0) onChange(Math.round(next) * unit);
        }}
      />
      <select
        aria-label={`${label} — unit`}
        className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
        disabled={disabled}
        value={unit}
        onChange={(e) => onChange(value * Number(e.target.value))}
      >
        <option value={1}>min</option>
        <option value={60}>hours</option>
        <option value={1440}>days</option>
      </select>
    </div>
  );
}
